// Demo mode, runs entirely client-side when the app is opened outside
// Telegram (no auth, no LLM round trip). Pulls 15 hand-curated ideas from
// /demo/ideas.json and replays them as if they came from the real streaming
// API, so the slot machine animation feel is identical.

import type {
  Competitor,
  DiceRoll,
  IdeaDTO,
  Lang,
  SpinStreamEvent,
} from "./api";

/** Field-emit order. Matches what the real LLM tends to emit (v2 prompt,
 *  6 fields). model_label included because the demo replays it as a
 *  field event so the modal's tech chip animates in. */
const FIELD_ORDER: (keyof IdeaDTO | "model_label")[] = [
  "title",
  "today_they",
  "instead_they_could",
  "who_pays_and_how_much",
  "distribution",
  "the_giant_in_the_room",
  // Legacy fields kept so the bundled demo/ideas.json (pre-variant-C)
  // still streams cleanly until it's regenerated.
  "subtitle",
  "model_label",
  "what_to_build",
  "who_pays",
  "how_to_get_customers",
  "main_risk",
];

/** Demo dice, fixed roll so the modal pill renders, but visually unused. */
const DEMO_DICE: DiceRoll = {
  experience:   { id: "mirror",       label: "BUILT 4 U" },
  wedge:        { id: "distribution", label: "DIST HACK" },
  voice:        { id: "founder",      label: "WARTIME" },
  horizon:      { id: "year",         label: "1Y" },
  competition:  { id: "underbelly",   label: "WEAK SPOT" },
  monetization: { id: "subscription", label: "SUB" },
};

let cache: Promise<IdeaDTO[]> | null = null;
function loadIdeas(): Promise<IdeaDTO[]> {
  if (!cache) {
    cache = fetch("/demo/ideas.json")
      .then((r) => (r.ok ? r.json() : []))
      .catch(() => [] as IdeaDTO[]);
  }
  return cache;
}

const DEMO_KEY = "saaslots.demo";

/**
 * Demo mode is now explicit. We used to enter it any time the app loaded
 * outside Telegram; that path is replaced by the real Telegram Login
 * Widget flow, so unauthenticated visitors land on a login screen instead.
 * Demo is still useful for marketing screenshots / sharing without auth,
 * so we keep it accessible via `?demo=1` and sticky for the tab session.
 */
export function isDemoMode(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get("demo") === "1") {
      try { sessionStorage.setItem(DEMO_KEY, "1"); } catch { /* ignore */ }
      return true;
    }
    if (url.searchParams.get("demo") === "0") {
      try { sessionStorage.removeItem(DEMO_KEY); } catch { /* ignore */ }
      return false;
    }
  } catch {
    /* ignore */
  }
  try {
    return sessionStorage.getItem(DEMO_KEY) === "1";
  } catch {
    return false;
  }
}

/** Pick a random demo idea matching the requested language (fallback: any). */
async function pickIdea(lang: Lang): Promise<IdeaDTO | null> {
  const all = await loadIdeas();
  if (all.length === 0) return null;
  const matching = all.filter((i) => i.language === lang);
  const pool = matching.length > 0 ? matching : all;
  return pool[Math.floor(Math.random() * pool.length)];
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Yields the same shape of events as `spinStream` so the UI doesn't care
 * which path is active. Timings are deliberately tighter than the real LLM
 * (TTFB ~80 ms, full body ~1.4 s) so the demo feels snappy.
 */
export async function* demoSpinStream(opts: {
  language: Lang;
  signal?: AbortSignal;
}): AsyncGenerator<SpinStreamEvent> {
  const aborted = () => !!opts.signal?.aborted;
  await sleep(80);
  if (aborted()) return;

  const idea = await pickIdea(opts.language);
  if (!idea) {
    yield { type: "error", code: "demo_missing" };
    return;
  }

  // meta
  yield {
    type: "meta",
    model: idea.model,
    model_label: idea.model_label,
    tier: idea.tier,
    language: idea.language,
    dice: DEMO_DICE,
  };

  // Stream fields with small randomised delays. Total ~1100,1400 ms.
  for (const key of FIELD_ORDER) {
    if (aborted()) return;
    const value = (idea as any)[key];
    if (value === undefined || value === null || value === "") continue;
    yield { type: "field", key: key as any, value };
    await sleep(60 + Math.random() * 70);
  }

  if (aborted()) return;
  yield { type: "idea", idea };

  // Empty competitors, keeps the section in "done, none found" state.
  await sleep(200);
  if (aborted()) return;
  const competitors: Competitor[] = [];
  yield { type: "competitors", competitors };

  yield { type: "done" };
}
